/**
 * MenuHeader — sticky top navigation bar for the public menu.
 * Contains: logo, table badge, auth button, reservation, language, rating, cart button.
 */
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { ShoppingBag, Star, CalendarDays } from "lucide-react";
import { CustomerLoginButton } from "@/components/public/CustomerAuth";
import { LanguageToggle } from "@/components/LanguageSelector";

export function MenuHeader({
  restaurant, tc, pr, pg, pb,
  tableName, tableNumber,
  cart, cartTotal, cartCount, openCheckout, fmt,
  customerSession, setShowAuthModal,
  setShowReservation, setReservationConfirmed,
  logoUrl, logoClass,
}) {
  const { t } = useTranslation();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-sm border-b shadow-sm" style={{ backgroundColor: `${tc.headerBg}f2`, borderColor: `${tc.text}10` }}>
      <div className="max-w-5xl mx-auto px-2 sm:px-4 py-2 sm:py-2.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-2.5 min-w-0 flex-shrink">
          {logoUrl && <img src={logoUrl} alt="Logo" className={`w-8 h-8 sm:w-12 sm:h-12 object-contain flex-shrink-0 ${logoClass}`} data-testid="menu-logo" onError={(e) => { e.target.style.display = "none"; }} />}
          <div className="min-w-0">
            <h1 className="font-bold text-xs sm:text-base leading-tight truncate max-w-[120px] sm:max-w-none" style={{ color: tc.text }}>{restaurant?.name || t("restaurant.title")}</h1>
            <div className="flex items-center gap-1.5">
              {(tableName || tableNumber) && <Badge className="text-[10px] px-1.5 py-0" style={{ backgroundColor: `rgba(${pr},${pg},${pb},0.1)`, color: tc.primary, borderColor: `rgba(${pr},${pg},${pb},0.25)` }}>{tableName || `${t("delivery.table")} ${tableNumber}`}</Badge>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <div className="hidden xs:block">
            <CustomerLoginButton customerSession={customerSession} onClick={() => setShowAuthModal(true)} themeColor={tc.primary} />
          </div>
          <button onClick={() => { setShowReservation(true); setReservationConfirmed(false); }} className="flex px-2 sm:px-3.5 py-1.5 sm:py-2 rounded-full text-[10px] sm:text-xs font-bold items-center gap-1 sm:gap-1.5 transition shadow-sm text-white" style={{ backgroundColor: tc.primary }} data-testid="reserve-btn">
            <CalendarDays className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">{t('publicMenu.reserveTable')}</span>
            <span className="sm:hidden">{t('publicMenu.reserveShort')}</span>
          </button>
          <div className="hidden sm:block"><LanguageToggle className="bg-white/10 hover:bg-white/20 rounded-full" /></div>
          {restaurant?.rating && (
            <div className="flex items-center gap-1 text-sm bg-amber-50 px-1.5 sm:px-2 py-1 rounded-full border border-amber-200">
              <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-amber-500 fill-amber-500" />
              <span className="font-bold text-amber-700 text-[10px] sm:text-xs">{restaurant.rating.toFixed(1)}</span>
            </div>
          )}
          {cart.length > 0 && (
            <button onClick={openCheckout} className="relative text-white px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold flex items-center gap-1.5 transition shadow-sm" style={{ backgroundColor: tc.primary }} data-testid="header-cart-btn">
              <ShoppingBag className="w-4 h-4" /><span>{fmt(cartTotal)}</span>
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{cartCount}</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
